import React, { useMemo, useState, useRef, useCallback } from 'react';
import { LayoutChangeEvent, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text } from '~/components/gluestack/text';
import { Skeleton } from '@/components/gluestack/skeleton';
import { PhotoCard } from '~/components/domain/photos/PhotoCard';
import { Photo } from '~/models/Photo';
import { useMemberPreviewPhotos, buildProfilePhotoCarousel } from '@/hooks/useMemberPreviewPhotos';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { GridViewIcon } from '@hugeicons-pro/core-stroke-rounded';

const GAP = 10;

const EMPTY_PHOTOS: Photo[] = [];

export const PROFILE_PHOTOS_PREVIEW_SIZE_CONFIG = {
  sm: { tileSizePx: 80 },
  md: { tileSizePx: 96 },
  lg: { tileSizePx: 112 },
} as const;

export type ProfilePhotosPreviewSize = keyof typeof PROFILE_PHOTOS_PREVIEW_SIZE_CONFIG;

interface ProfilePhotosPreviewProps {
  memberId: number;
  publicPhotosCount: number;
  onSeeAllPress?: () => void;
  disableImageViewer?: boolean;
  includeProfilePhoto?: boolean;
  /** Profile photo identity, prepended to the carousel so the avatar is swipeable. */
  profilePhotoId?: number | null;
  profilePhotoUrl?: string;
  maxCount: number;
  showLoadingSkeleton?: boolean;
  skeletonCount?: number;
  size?: ProfilePhotosPreviewSize;
  withDetails?: boolean; 
}

export function ProfilePhotosPreview({
  memberId,
  publicPhotosCount,
  onSeeAllPress,
  disableImageViewer = false,
  includeProfilePhoto = false,
  profilePhotoId,
  profilePhotoUrl,
  maxCount,
  showLoadingSkeleton = false,
  skeletonCount: skeletonCountProp,
  size = 'md',
  withDetails = true,
}: ProfilePhotosPreviewProps) {
  const { t } = useTranslation();
  const [containerWidth, setContainerWidth] = useState(0);
  const sizeConfig = PROFILE_PHOTOS_PREVIEW_SIZE_CONFIG[size];

  const onContainerLayout = (e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    if (w > 0) setContainerWidth(w);
  };

  const dynamicMaxPhotos = useMemo(() => {
    if (containerWidth <= 0) return 3;
    const tileSize = sizeConfig.tileSizePx;
    let count = Math.floor((containerWidth - tileSize - GAP) / (tileSize + GAP));
    if (size === 'sm') count += 1;
    return Math.max(1, count);
  }, [containerWidth, size, sizeConfig.tileSizePx]);

  const displayCount = useMemo(() => {
    if (containerWidth <= 0) return maxCount;
    return Math.min(dynamicMaxPhotos, maxCount);
  }, [containerWidth, dynamicMaxPhotos, maxCount]);

  const { data: recentPhotos, isPending } = useMemberPreviewPhotos(memberId, {
    maxCount,
    withDetails,
    enabled: publicPhotosCount > 0,
  });

  const filteredPhotos = useMemo(() => {
    if (!recentPhotos) return EMPTY_PHOTOS;
    return includeProfilePhoto ? recentPhotos : recentPhotos.filter((p) => !p.IsProfilePhoto);
  }, [recentPhotos, includeProfilePhoto]);

  const hasMorePhotos = publicPhotosCount > displayCount + 1;

  const previewPhotos = useMemo(() => {
    return filteredPhotos.slice(0, hasMorePhotos ? displayCount : undefined);
  }, [filteredPhotos, displayCount, hasMorePhotos]);

  // The carousel always spans the full photo set with the profile photo first,
  // so swiping from a preview thumbnail reaches the same photos as tapping the
  // profile photo. The thumbnails above just hide the profile photo from view.
  const carouselPhotos = useMemo(
    () => buildProfilePhotoCarousel(recentPhotos, profilePhotoId, profilePhotoUrl),
    [recentPhotos, profilePhotoId, profilePhotoUrl]
  );
  const carouselPhotosRef = useRef<Photo[]>(EMPTY_PHOTOS);
  carouselPhotosRef.current = carouselPhotos;
  const getPhotosForCarousel = useCallback(() => carouselPhotosRef.current, []);

  if (isPending && showLoadingSkeleton) {
    const count = skeletonCountProp ?? maxCount;
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerClassName="gap-2.5"
      >
        {Array.from({ length: count }).map((_, i) => (
          <Skeleton key={i} className="rounded-lg" style={{ width: sizeConfig.tileSizePx, height: sizeConfig.tileSizePx }} />
        ))}
      </ScrollView>
    );
  }

  if (previewPhotos.length === 0) return null;

  return (
    <View className="w-full" onLayout={onContainerLayout}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerClassName="gap-2.5"
      >
      {previewPhotos.map((photo) => (
        <View key={photo.Id} style={{ width: sizeConfig.tileSizePx, height: sizeConfig.tileSizePx }}>
          <PhotoCard photo={photo} getPhotosForCarousel={getPhotosForCarousel} disableImageViewer={disableImageViewer} />
        </View>
      ))}
      {hasMorePhotos && onSeeAllPress && (
        <Pressable
          onPress={onSeeAllPress}
          className="items-center justify-center rounded-lg bg-background-50"
          style={{ width: sizeConfig.tileSizePx, height: sizeConfig.tileSizePx }}
        >
          <HugeIconsWrapper icon={GridViewIcon} size={24} className="text-typography-500" />
          <Text size="xs" className="mt-2 text-center text-typography-500">
            {t('profile.seeAllPhotos', 'See all')}
          </Text>
        </Pressable>
      )}
      </ScrollView>
    </View>
  );
}
